import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  MobileStepper,
  Avatar,
} from '@mui/material';
import {
  AutoStories as BookIcon,
  GridOn as GridIcon,
  QuestionAnswer as QAIcon,
  NavigateNext as NextIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

const STORAGE_KEY = 'onboarding_done';

const steps = [
  {
    icon: <BookIcon sx={{ fontSize: 48 }} />,
    iconColor: '#7e57c2',
    title: '歡迎來到「我的好運氣」',
    body: '這是一套以紫微斗數為基礎的個人命盤分析系統。\n\n根據你的出生時間，系統已為你建立專屬命盤，並可回答超過 90 個關於感情、事業、財運、健康的深度問題。',
  },
  {
    icon: <GridIcon sx={{ fontSize: 48 }} />,
    iconColor: '#1976d2',
    title: '什麼是命盤？',
    body: '命盤是一張 4×4 的宮位圖，共 12 個宮位，每個宮位代表人生不同面向：\n\n• 命宮 — 個性與整體運勢\n• 夫妻宮 — 感情與婚姻\n• 財帛宮 — 財運與金錢\n• 事業宮 — 職涯與工作\n\n你可以在「查看命盤」頁面看到你的完整命盤圖。',
  },
  {
    icon: <QAIcon sx={{ fontSize: 48 }} />,
    iconColor: '#388e3c',
    title: '如何開始？',
    body: '有三種方式探索你的命盤：\n\n1. 主頁的「今日推薦問題」— 系統依本月運勢自動推薦\n2. 「問題查詢」— 瀏覽所有 90+ 個問題\n3. 主頁的「每日生活建議」— 飲食、穿搭、學習貼心安排\n\n記得先驗證 Email 即可領取 499 點體驗點數！',
  },
];

interface OnboardingWizardProps {
  userCreatedAt: string;
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ userCreatedAt }) => {
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;
    // 只對 7 天內的新使用者顯示
    const createdMs = new Date(userCreatedAt).getTime();
    const nowMs = Date.now();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    if (nowMs - createdMs <= sevenDaysMs) {
      setOpen(true);
    }
  }, [userCreatedAt]);

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(s => s + 1);
    }
  };

  const handleFinish = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setOpen(false);
    navigate('/dashboard');
  };

  const handleSkip = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setOpen(false);
  };

  const isLast = activeStep === steps.length - 1;
  const step = steps[activeStep];

  return (
    <Dialog open={open} maxWidth="xs" fullWidth>
      <DialogContent sx={{ pb: 1, pt: 3, px: 3 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} mb={2}>
          <Avatar sx={{ bgcolor: step.iconColor, width: 72, height: 72 }}>
            {step.icon}
          </Avatar>
          <Typography variant="h6" fontWeight="bold" textAlign="center">
            {step.title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}
          >
            {step.body}
          </Typography>
        </Box>

        <MobileStepper
          variant="dots"
          steps={steps.length}
          position="static"
          activeStep={activeStep}
          sx={{ justifyContent: 'center', bgcolor: 'transparent', mb: 1 }}
          nextButton={<Box />}
          backButton={<Box />}
        />

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button size="small" color="inherit" onClick={handleSkip} sx={{ color: 'text.disabled' }}>
            跳過
          </Button>
          <Button
            variant="contained"
            size="small"
            endIcon={isLast ? <CheckIcon /> : <NextIcon />}
            onClick={isLast ? handleFinish : handleNext}
          >
            {isLast ? '前往主頁' : '下一步'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingWizard;
